import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import SearchableSelect from "../../components/SearchableSelect";
import { apiRequest } from "../../lib/api";
import { labelFromCode } from "../shared/format";

export type Relation = "CUSTOMER" | "VENDOR" | "BOTH";
type Organization = { organization_id: string; name: string; organization_type: string };

export default function AddOrganizationParty({ organizationId, relation, onSaved, onCancel }: {
  organizationId: string; relation: Relation; onSaved: (partyId: string) => void; onCancel: () => void;
}) {
  const root = `/platform/crm/organizations/${encodeURIComponent(organizationId)}/parties`;
  const [mode, setMode] = useState<"coprint" | "private">("coprint");
  const [relationship, setRelationship] = useState(relation);
  const [target, setTarget] = useState("");
  const [name, setName] = useState("");
  const [entityType, setEntityType] = useState<"BUSINESS" | "INDIVIDUAL">("BUSINESS");
  const [gstin, setGstin] = useState("");
  const [contactName, setContactName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const organizations = useQuery({
    queryKey: ["ops-crm-discovery", organizationId],
    queryFn: async () => {
      const all: Organization[] = [];
      for (let page = 0; ; page++) {
        const result = await apiRequest<{ organizations: Organization[]; has_more: boolean }>(`${root}/coprint-organizations?page=${page}`);
        all.push(...result.organizations);
        if (!result.has_more) return all;
      }
    }
  });
  const mutation = useMutation({
    mutationFn: () => apiRequest<{ party_id: string }>(mode === "coprint" ? `${root}/imports` : root, {
      method: "POST",
      body: mode === "coprint" ? { organization_id: target, relation_type: relationship } : {
        name: name.trim(), entity_type: entityType, relation_type: relationship, status: "ACTIVE",
        gstin: entityType === "BUSINESS" ? gstin.trim() : null,
        primary_contact: { name: contactName.trim(), mobile: mobile.trim(), email: email.trim() || null, primary_contact: true }
      }
    }),
    onSuccess: result => onSaved(result.party_id)
  });
  return <form className="pa-admin-form pa-crm-add-form" onSubmit={event => { event.preventDefault(); mutation.mutate(); }}>
    <h3>Add customer or vendor</h3>
    <fieldset className="pa-detail-fields" disabled={mutation.isPending}>
      <div className="pa-detail-field-grid">
        <label>Source<select value={mode} onChange={event => { setMode(event.target.value as typeof mode); mutation.reset(); }}>
          <option value="coprint">From Coprint</option><option value="private">Create private record</option>
        </select></label>
        <label>Relationship<select value={relationship} onChange={event => setRelationship(event.target.value as Relation)}>
          <option value="CUSTOMER">Customer</option><option value="VENDOR">Vendor</option><option value="BOTH">Customer & vendor</option>
        </select></label>
      </div>
      {mode === "coprint" ? <>
        <div className="pa-crm-organization-choice"><span>Coprint organization</span>
          <SearchableSelect label="Coprint organization" value={target} onChange={setTarget}
            options={(organizations.data ?? []).map(org => ({ value: org.organization_id, label: org.name, description: org.organization_type === "SMB" ? "SMB" : labelFromCode(org.organization_type) }))}
            disabled={mutation.isPending || organizations.isPending || organizations.isError}
            placeholder={organizations.isPending ? "Loading organizations…" : "Select an organization"} />
        </div>
        <p className="pa-form-note">Browse the list or search by name. Official details stay up to date from Coprint. Adding an existing record updates its relationship or restores it.</p>
        {organizations.isError && <p className="pa-form-error" role="alert">Could not load organizations. <button type="button" className="pa-secondary-button" onClick={() => void organizations.refetch()}>Retry</button></p>}
      </> : <div className="pa-detail-field-grid">
        <label>Name<input required maxLength={180} value={name} onChange={event => setName(event.target.value)} /></label>
        <label>Entity type<select value={entityType} onChange={event => { setEntityType(event.target.value as typeof entityType); if (event.target.value === "INDIVIDUAL") setGstin(""); }}>
          <option value="BUSINESS">Business</option><option value="INDIVIDUAL">Individual</option>
        </select></label>
        <label>{entityType === "BUSINESS" ? "GSTIN (required)" : "GSTIN (not applicable)"}<input required={entityType === "BUSINESS"} disabled={entityType === "INDIVIDUAL"} maxLength={15} pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]" title="Enter a valid 15-character GSTIN." value={gstin} onChange={event => setGstin(event.target.value.toUpperCase())} /></label>
        <label>Primary contact name<input required maxLength={150} value={contactName} onChange={event => setContactName(event.target.value)} /></label>
        <label>Primary contact number (required)<input required type="tel" maxLength={30} value={mobile} onChange={event => setMobile(event.target.value)} placeholder="Include country code" /></label>
        <label>Contact email<input type="email" maxLength={254} value={email} onChange={event => setEmail(event.target.value)} /></label>
      </div>}
    </fieldset>
    {mutation.isError && <p className="pa-form-error" role="alert">{mutation.error instanceof Error ? mutation.error.message : "Could not add this record."}</p>}
    <div className="pa-detail-actions">
      <button type="button" className="pa-secondary-button" disabled={mutation.isPending} onClick={onCancel}>Cancel</button>
      <button className="pa-primary-button" disabled={mutation.isPending || (mode === "coprint" ? !target || organizations.isPending || organizations.isError : !name.trim())}>
        {mutation.isPending ? "Adding…" : relationship === "BOTH" ? "Add customer & vendor" : relationship === "CUSTOMER" ? "Add customer" : "Add vendor"}
      </button>
    </div>
  </form>;
}
